import React, { useState } from 'react';
import { MapPin, Edit2, Trash2, CheckCircle, Building, Save, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SiteOffice } from '@/services/siteOfficeService';

interface CustomSiteOfficeCardProps {
  office: SiteOffice;
  isSelected: boolean;
  onSelect: (officeName: string) => void;
  onEdit: (officeId: string, newName: string) => Promise<void>;
  onDelete: (officeId: string) => Promise<void>;
}

const CustomSiteOfficeCard = ({ 
  office, 
  isSelected, 
  onSelect, 
  onEdit, 
  onDelete 
}: CustomSiteOfficeCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(office.name);
  const [isLoading, setIsLoading] = useState(false);

  const handleEdit = async () => {
    if (editName.trim().length < 3) return;
    
    setIsLoading(true);
    try {
      console.log('🔄 Calling onEdit with:', office.id, editName.trim());
      await onEdit(office.id, editName.trim());
      setIsEditing(false);
      console.log('✅ Edit completed successfully');
    } catch (error) {
      console.error('❌ Error editing site office:', error);
      // Reset to original name on error
      setEditName(office.name);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const confirmMessage = `Apakah Anda yakin ingin menghapus "${office.name}"?\n\nTindakan ini tidak dapat dibatalkan.`;
    if (window.confirm(confirmMessage)) {
      setIsLoading(true);
      try {
        console.log('🗑️ Calling onDelete with:', office.id);
        await onDelete(office.id);
        console.log('✅ Delete completed successfully');
      } catch (error) {
        console.error('❌ Error deleting site office:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('✏️ Edit button clicked for office:', office.id, office.name);
    setIsEditing(true);
    setEditName(office.name); // Reset to current name
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('❌ Cancel edit clicked');
    setIsEditing(false);
    setEditName(office.name); // Reset to original name
  };

  const handleSaveEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('💾 Save button clicked, calling handleEdit');
    handleEdit();
  };

  const handleInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && editName.trim().length >= 3) {
      handleEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit(e as any);
    }
  };

  // Always treat as custom office for this component
  const isCustomOffice = true;

  return (
    <div
      className={`group relative p-6 border-2 rounded-xl cursor-pointer transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg ${
        isSelected 
          ? 'border-sky-500 bg-gradient-to-br from-sky-50 to-sky-50 shadow-md' 
          : 'border-gray-200 bg-white hover:border-sky-300 hover:bg-sky-50/30'
      } ${isLoading ? 'opacity-70 pointer-events-none' : ''}`}
      onClick={() => !isEditing && !isLoading && onSelect(office.name)}
    >
      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
          <div className="flex items-center space-x-2 text-sky-600">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm font-medium">
              {isEditing ? 'Menyimpan...' : 'Menghapus...'}
            </span>
          </div>
        </div>
      )}

      {/* Selection Indicator */}
      <div className="flex items-start justify-between mb-4">
        <div className={`relative w-6 h-6 rounded-full border-2 transition-all duration-200 ${
          isSelected 
            ? 'border-sky-500 bg-sky-500' 
            : 'border-gray-300 group-hover:border-sky-400'
        }`}>
          {isSelected && (
            <CheckCircle className="w-6 h-6 text-white absolute -top-0.5 -left-0.5" />
          )}
        </div>

        {/* Custom Badge and Actions */}
        <div className="flex items-center space-x-2">
          {/* Custom Badge - Always visible */}
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-sky-100 text-sky-700 border border-sky-200 rounded-full text-sm font-medium">
            <span>Custom</span>
          </div>
          
          {/* Edit/Delete Actions - Always visible for custom offices */}
          {!isEditing && (
            <div className="flex items-center space-x-1 opacity-100">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleEditClick}
                className="h-8 w-8 p-0 hover:bg-blue-100 transition-all duration-200"
                disabled={isLoading}
                type="button"
                title="Edit site office"
              >
                <Edit2 className="w-4 h-4 text-blue-600" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                className="h-8 w-8 p-0 hover:bg-red-100 text-red-600 transition-all duration-200"
                disabled={isLoading}
                type="button"
                title="Delete site office"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Save/Cancel Actions when Editing */}
          {isEditing && (
            <div className="flex items-center space-x-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSaveEdit}
                className="h-8 w-8 p-0 hover:bg-green-100 text-green-600 transition-all duration-200"
                disabled={isLoading || editName.trim().length < 3}
                type="button"
                title="Save changes"
              >
                <Save className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancelEdit}
                className="h-8 w-8 p-0 hover:bg-gray-100 transition-all duration-200"
                disabled={isLoading}
                type="button"
                title="Cancel editing"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Office Info */}
      <div className="space-y-3">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            isSelected 
              ? 'bg-sky-500 text-white' 
              : 'bg-gray-100 text-gray-600 group-hover:bg-sky-100 group-hover:text-sky-600'
          } transition-all duration-200`}>
            <Building className="w-6 h-6" />
          </div>
          <div className="flex-1">
            {isEditing ? (
              <div className="space-y-2">
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={handleInputKeyPress}
                  className="font-semibold text-lg border-sky-300 focus:border-sky-500 focus:ring-sky-500"
                  minLength={3}
                  onClick={(e) => e.stopPropagation()}
                  disabled={isLoading}
                  placeholder="Enter site office name..."
                  autoFocus
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    Minimal 3 karakter • Enter untuk simpan • Esc untuk batal
                  </p>
                  <span className={`text-xs font-medium ${
                    editName.trim().length >= 3 ? 'text-green-600' : 'text-red-500'
                  }`}>
                    {editName.trim().length}/3
                  </span>
                </div>
              </div>
            ) : (
              <>
                <h3 className="font-semibold text-lg text-gray-900 group-hover:text-sky-700 transition-colors">
                  {office.name}
                </h3>
                {office.location && (
                  <div className="flex items-center space-x-1 mt-1">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">{office.location}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Office ID */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <span className="text-sm font-medium text-gray-500">Office ID:</span>
          <span className="text-sm font-mono font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded">
            {office.name.toUpperCase().replace(/\s+/g, '-')}
          </span>
        </div>

        {/* Action Hint */}
        {!isEditing && (
          <div className="text-xs text-gray-400 text-center pt-2 border-t border-gray-100">
            💡 Klik tombol edit untuk mengubah nama atau hapus untuk menghapus
          </div>
        )}
      </div>

      {/* Hover Effect Overlay */}
      <div className={`absolute inset-0 rounded-xl transition-opacity duration-200 pointer-events-none ${
        isSelected 
          ? 'bg-gradient-to-br from-sky-500/10 to-sky-500/10' 
          : 'bg-gradient-to-br from-sky-500/0 to-sky-500/0 group-hover:from-sky-500/5 group-hover:to-sky-500/5'
      }`} />
    </div>
  );
};

export default CustomSiteOfficeCard;
